import React, { useEffect, useMemo, useState } from 'react';
import {
  ensureWorkTimers,
  findWorkTimer,
  liveElapsedSeconds,
  runWorkTimerAction,
  subscribeWorkTimers,
} from '../../services/workTimerStore';

const formatWatch = (totalSeconds) => {
  const secs = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const PlayIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M6.5 4.5v11l9-5.5-9-5.5z" />
  </svg>
);

const PauseIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M6 4h2.5v12H6V4zm5.5 0H14v12h-2.5V4z" />
  </svg>
);

const WorkItemTimer = ({
  targetType,
  targetId,
  noun = 'task',
  compact = false,
  disabled = false,
}) => {
  const [timer, setTimer] = useState(() => findWorkTimer(targetType, targetId));
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ensureWorkTimers();
    return subscribeWorkTimers(() => {
      setTimer(findWorkTimer(targetType, targetId));
    });
  }, [targetType, targetId]);

  const running = Boolean(timer?.running);
  const elapsed = useMemo(() => liveElapsedSeconds(timer), [timer, tick]);
  const started = elapsed > 0 || running || Boolean(timer);

  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  if (!targetType || !targetId || disabled) return null;

  const run = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      await runWorkTimerAction(targetType, targetId, action);
    } catch {
      // Keep last known timer state.
    } finally {
      setBusy(false);
    }
  };

  if (!started) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          run('start');
        }}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
        title={`I am starting this ${noun}`}
      >
        <PlayIcon className="w-3 h-3" />
        {compact ? 'Start' : `I'm starting this ${noun}`}
      </button>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg border px-1.5 py-0.5 ${
        running
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
          : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${running ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
      <span className="font-mono text-[11px] font-semibold tabular-nums leading-none">
        {formatWatch(elapsed)}
      </span>
      <button
        type="button"
        onClick={() => run(running ? 'pause' : 'resume')}
        disabled={busy}
        className="p-0.5 rounded hover:bg-white/60 dark:hover:bg-black/20 disabled:opacity-50"
        title={running ? 'Pause' : 'Resume'}
      >
        {running ? <PauseIcon className="w-3 h-3" /> : <PlayIcon className="w-3 h-3" />}
      </button>
    </span>
  );
};

export default WorkItemTimer;
